/**
 * Opening an interview directly (refresh, link, notification), its request
 * waited for the shell to load the user and permissions and then for the lazy
 * details chunk; the application and contact requests then waited for it in
 * turn to learn their ids. Here the interview request and the chunk start
 * during boot, and the moment the interview lands its application, notes and
 * contact are warmed — the same requests `useWarmInterviewDetails` warms from
 * the list.
 *
 * The query key must match `InterviewDetails` (`['interview', id]`, id as in
 * the URL) so the page reads this entry instead of fetching again.
 */
import { queryClient } from '@/lib/queryClient';
import { store } from '@/store';
import { getInterviewById } from '@/api/interviews';
import {
  prefetchApplication,
  prefetchApplicationNotes,
} from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { contactApi } from '@/store/query/contacts';
import { preloadInterviewDetailsRoute } from './preloadInterviewDetailsRoute';

const DETAILS_PATH = /^\/interviews\/(\d+)(?:\/(?:prep|live|coach))?\/?$/;

export const interviewIdFromDetailsPath = (pathname: string): string | null =>
  DETAILS_PATH.exec(pathname)?.[1] ?? null;

type InterviewPayload = {
  data?: { ats_application_id?: number | string; candidate?: { id?: number } };
};

let requested = false;

export const prefetchInterviewDetailsOnBoot = (
  pathname: string = window.location.pathname
): void => {
  const interviewId = interviewIdFromDetailsPath(pathname);
  if (!interviewId || requested) return;
  requested = true;

  preloadInterviewDetailsRoute();

  // Without a session the call would only bounce off auth.
  try {
    if (!localStorage.getItem('apiv1Auth')) return;
  } catch {
    return;
  }

  queryClient
    .fetchQuery({
      queryKey: ['interview', interviewId],
      queryFn: () => getInterviewById(interviewId),
    })
    .then((response) => {
      const interview = (response as InterviewPayload)?.data;
      const applicationId = interview?.ats_application_id;
      if (applicationId) {
        void prefetchApplication(queryClient, applicationId);
        void prefetchApplicationNotes(queryClient, applicationId);
      }
      const contactId = interview?.candidate?.id;
      if (contactId) {
        store.dispatch(
          contactApi.util.prefetch('getContactById', { contactId }, {})
        );
      }
    })
    .catch(() => {
      // The page fetches again on mount and owns the error state.
    });
};
